import type { JobProgressMessage } from '@quipex/core/realtime';
import {
    mapActivityJob,
    type ActivityIdentity,
    type ActivityJobUpdate,
    type ActivitySession,
} from '../activity-journal';
import { reduceActivity } from '../activity-journal/reducer';
import { decideActivityToast } from '../activity-journal/toast-policy';
import type { ActivityToastDecisionSnapshot } from './action-contract';

export interface ActivityJobObservation {
    readonly session: ActivitySession;
    readonly toastDecision: ActivityToastDecisionSnapshot | null;
}

export function observeActivityJob(input: {
    readonly identity: ActivityIdentity;
    readonly activeIdentityVersion: number;
    readonly session: ActivitySession;
    readonly message: JobProgressMessage;
}): ActivityJobObservation | null {
    const mappedUpdate = mapActivityJob(input.message);
    if (!mappedUpdate) return null;

    return observeActivityJobUpdate({ ...input, update: mappedUpdate });
}

export function observeActivityJobUpdate(input: {
    readonly identity: ActivityIdentity;
    readonly activeIdentityVersion: number;
    readonly session: ActivitySession;
    readonly update: ActivityJobUpdate;
}): ActivityJobObservation {
    const { update } = input;
    const previousItem = input.session.items.find(
        (item) => item.key === update.key
    );
    const nextSession = reduceActivity(input.session, {
        type: 'job-observed',
        update,
    });
    const nextItem = nextSession.items.find((item) => item.key === update.key);
    const toastDecision = decideActivityToast({
        source: 'live',
        previous: previousItem ?? null,
        next: nextItem ?? null,
    });

    return {
        session: nextSession,
        toastDecision: toastDecision.eligible
            ? {
                  ...toastDecision,
                  identity: input.identity,
                  activeIdentityVersion: input.activeIdentityVersion,
                  buildingPublicId:
                      nextItem?.jobs.find((job) => job.buildingPublicId)
                          ?.buildingPublicId ?? null,
                  action:
                      toastDecision.intent && nextItem
                          ? {
                                identity: input.identity,
                                activeIdentityVersion:
                                    input.activeIdentityVersion,
                                intent: toastDecision.intent,
                                version: nextItem.updatedAtUtc,
                            }
                          : null,
              }
            : null,
    };
}
